import { DataService } from '../services/DataService';
import {
  buildPrettyWorkspacePath,
  buildPrettyWorkspaceEditPath,
  buildPrettyProjectPath,
  buildPrettyTaskPath,
  workspaceDetailSectionFromTab,
} from './prettyRoutes';
import { buildCompanyPath, clientCompanySlug, employerCompanySlug } from '../features/contacts/companies/companyRoutes';

const appendQuery = (pathname: string, params?: Record<string, string | undefined>) => {
  if (!params) return pathname;

  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) query.set(key, value);
  });

  const queryString = query.toString();
  return queryString ? `${pathname}?${queryString}` : pathname;
};

export const buildProjectPathById = (
  projectId: string,
  params?: { tab?: string; item?: string }
) => {
  const project = DataService.getProject(projectId);
  const client = project ? DataService.getCompany(project.clientId) : null;

  const pathname =
    project && client
      ? buildPrettyProjectPath({
          clientName: client.name,
          projectName: project.name,
        })
      : `/projects/${projectId}`;

  return appendQuery(pathname, params);
};

export const buildTaskPathById = (taskId: string) => {
  const task = DataService.getTask(taskId);
  return task
    ? buildPrettyTaskPath({ taskTitle: task.title, taskId: task.id })
    : `/tasks?taskId=${taskId}`;
};

// Workspaces and employers are records on the Companies page, so both live under /contacts/<slug>.
// The old /workspaces/<slug> and /employers/<slug> addresses still parse and are rewritten to these.
export const buildWorkspacePathById = (
  clientId: string,
  params?: { activeTab?: string },
) => {
  const client = DataService.getCompany(clientId);
  if (client?.type !== 'client') return `/workspaces/${clientId}`;
  if (params?.activeTab === 'resource-library') {
    return buildPrettyWorkspacePath({ workspaceName: client.name, activeTab: params.activeTab });
  }
  const employerNames = DataService.getMainContractors().map((e: { name: string }) => e.name);
  return buildCompanyPath(
    clientCompanySlug(client.name, employerNames),
    workspaceDetailSectionFromTab(params?.activeTab) ?? undefined,
  );
};

// URL for a drilled-in employer profile. `section` names the page the sidebar
// has selected; Details is the default and is left off. Falls back to the id
// when the employer can't be resolved, same as buildWorkspacePathById.
export const buildEmployerPathById = (
  employerId: string,
  params?: { section?: string },
) => {
  const employer = DataService.getMainContractor(employerId);
  if (!employer) {
    return params?.section && params.section !== 'details'
      ? `/employers/${employerId}/${params.section}`
      : `/employers/${employerId}`;
  }
  return buildCompanyPath(employerCompanySlug(employer.name), params?.section);
};

// URL for the inline-edit pane (split-view) on the workspaces directory.
// Falls back to /workspaces/{id}/edit when the company can't be resolved
// to a name (shouldn't happen in practice, but keeps the suffix consistent).
export const buildWorkspaceEditPathById = (clientId: string) => {
  const client = DataService.getCompany(clientId);
  return client?.type === 'client'
    ? buildPrettyWorkspaceEditPath({ workspaceName: client.name })
    : `/workspaces/${clientId}/edit`;
};
